import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { isEmpty } from 'lodash'

import { CogitationDog } from '@/components/ai/CogitationDog'
import { KodiToolMessage } from './KodiToolMessage'
import { KODI_THINKING_PHRASES } from '../kodi.constants'

const THINKING_PHRASE_INTERVAL_MS = 5000

const randomPhraseIndex = () => Math.floor(Math.random() * KODI_THINKING_PHRASES.length)

/**
 * The in-turn "Thinking…" loader: the CogitationDog animation, a status line, and a
 * collapsible drawer of the turn's tool steps. Owns its own open + phrase-cycling state
 * so it resets each turn (it unmounts when the turn ends).
 *
 * @param {object[]} props.steps - narrated tool-step messages for the current turn
 * @param {string} props.activeRationale - latest tool rationale to show, if any
 * @param {boolean} props.hidden - true while the answer streams (kept mounted, hidden)
 */
export const KodiTurnLoader = ({ steps, activeRationale, hidden }) => {
  const [open, setOpen] = useState(false)
  const [phraseIndex, setPhraseIndex] = useState(randomPhraseIndex)

  const hasSteps = !isEmpty(steps)
  // Cycle a dog-themed phrase only before a real rationale lands and while the loader
  // (not the streamed answer) is showing.
  const showPhrase = !hidden && isEmpty(activeRationale)

  useEffect(() => {
    if (!showPhrase) {
      return undefined
    }
    const intervalId = setInterval(() => {
      // Jump a random distance so the phrase order varies and never repeats back-to-back.
      setPhraseIndex(
        (prior) =>
          (prior + 1 + Math.floor(Math.random() * (KODI_THINKING_PHRASES.length - 1))) %
          KODI_THINKING_PHRASES.length
      )
    }, THINKING_PHRASE_INTERVAL_MS)
    return () => clearInterval(intervalId)
  }, [showPhrase])

  return (
    <div className={classNames('cowork-thinking', { 'cowork-thinking--hidden': hidden })}>
      <button
        type="button"
        className="cowork-thinking__toggle flex-align-center"
        onClick={() => setOpen((prior) => !prior)}
        aria-expanded={open}
        aria-label="Toggle tool steps"
        disabled={!hasSteps}
      >
        <span className="kodi-loading flex-column">
          <span className="kodi-loading__row flex-align-center">
            <span className="kodi-turn-loader">
              <CogitationDog />
            </span>
            <span className="kodi-loading__name">Kodi</span>
          </span>
          <span className="kodi-loading__status">
            {activeRationale || KODI_THINKING_PHRASES[phraseIndex]}
          </span>
        </span>
      </button>
      {open && hasSteps && (
        <div className="cowork-tool-group__items">
          {steps.map((message) => (
            <KodiToolMessage key={message.id} message={message} />
          ))}
        </div>
      )}
    </div>
  )
}
